/*@jsxRuntime automatic @jsxImportSource react*/
import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
import Head from 'next/head';
import styled from 'styled-components';
import {Columns, UpdatedDate, Wrapper} from '@/components/terms/styles';
export const meta = {
  title: 'Cookie Notice'
};
export const OptOutLink = ({children, onOpen}) => {
  const _components = Object.assign({
    span: "span",
    button: "button"
  }, _provideComponents());
  return _jsx(_components.span, {
    children: _jsx(_components.button, {
      onClick: onOpen,
      children: children
    })
  });
};
function _createMdxContent(props) {
  const _components = Object.assign({
    h1: "h1",
    h2: "h2",
    p: "p",
    h3: "h3",
    strong: "strong",
    a: "a",
    ul: "ul",
    li: "li"
  }, _provideComponents(), props.components);
  return _jsxs(Wrapper, {
    children: [_jsx(_components.h1, {
      children: "Calm Cookie and Pixel Notice"
    }), _jsx(UpdatedDate, {
      children: "Last Updated: November 7, 2024"
    }), _jsx(_components.h2, {
      children: "Information Collected Using Cookies, Pixels, and Other Web Technologies"
    }), _jsx(_components.p, {
      children: "Like many organizations, we, as well as third parties we work with that provide content, advertising, or other functionality for us, may use cookies, pixel tags, local storage, and other technologies (“Technologies”) to automatically collect information through our websites, mobile applications, and other online products and services (“Services”). We use Technologies that are essentially small data files that may be placed on the device you use, webpages you access, or emails you open to access the Services."
    }), _jsx(_components.h3, {
      children: "Here’s a description of some of the Technologies we use:"
    }), _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Cookies"
      })
    }), _jsx(_components.p, {
      children: "“Cookies” are small text files that are placed on your device when you access the Services. We may use both session cookies and persistent cookies to identify that you’ve logged in to the Services and to tell us how and when you interact with the Services. Unlike persistent cookies, session cookies are deleted when you log off from the Services and close your browser."
    }), _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Pixels"
      })
    }), _jsx(_components.p, {
      children: "“Pixels”/“Web Beacons” (also known as web bugs, pixel tags, or clear GIFs) are tiny transparent graphics with a unique identifier that may be included on the Services and emails for several purposes, including to deliver or communicate with cookies, to track and measure performance, and to monitor views and other activities. Unlike cookies, which are stored on the hard drive of your computer or mobile device by a website, pixel tags are embedded invisibly on webpages or within HTML formatted emails."
    }), _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Widgets"
      })
    }), _jsx(_components.p, {
      children: "“Social Media Widgets,” such as the Facebook “Like” button and the LinkedIn “Share this” button, may be on our website. These features may collect your IP address, which page you visit on our website, and set a cookie to enable the feature to function properly. The data collected from your interactions with these features is governed by the privacy policy of the company providing them."
    }), _jsx(_components.h3, {
      children: "Categories of Technologies"
    }), _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "First Party Technologies and Third Party Technologies"
      })
    }), _jsx(_components.p, {
      children: "First party Technologies are Technologies that are placed on your device by the Service you are using. Third party Technologies are placed on your device by a third party via a Service. Please note that we do not control the collection or further use of data by third parties."
    }), _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Operationally Necessary Technologies"
      })
    }), _jsx(_components.p, {
      children: "We may use Technologies that are necessary to the operation of the Services, such as to recognize subscribers, help protect security, or prevent fraud. These are essential to the core functioning of our Services, so if you set your browser to block Technologies, some parts of our Services may not work properly."
    }), _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Performance Related Technologies"
      })
    }), _jsx(_components.p, {
      children: "We may use Technologies to assess the performance of the Services, including as part of our analytic practices, to help us understand how our visitors use our Services, determine if you have interacted with our messaging, determine whether you have viewed an item or link, or to improve our Services."
    }), _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Functionality Related Technologies"
      })
    }), _jsx(_components.p, {
      children: "We may use Technologies that allow us to offer you enhanced functionality when accessing or using the Services. This may include keeping track of your specified preferences, interests, or past items viewed so that we may enhance the presentation of content on the Services."
    }), _jsx(_components.p, {
      children: _jsx(_components.strong, {
        children: "Advertising or Targeting Related Technologies"
      })
    }), _jsx(_components.p, {
      children: "We may use Technologies to deliver content, including ads, relevant to your interests on our Services or on third party services, and to better understand the effectiveness of our ads or content recommendations."
    }), _jsx(_components.h3, {
      children: "Managing Preferences"
    }), _jsx(_components.p, {
      children: "You may manage your preferences when using our Services by using the controls available in the footer (or equivalent portion) of our Services or, where applicable, when you first use the relevant Service."
    }), _jsx(_components.p, {
      children: "Additionally, you may stop or restrict the placement of Technologies on your computer or remove them from your browser by adjusting your web browser preferences."
    }), _jsxs(_components.p, {
      children: ["Depending on where you are located, if you have a Calm account, you may opt out by selecting Your Privacy Choices in the footer of our website and by following the instructions under “Account Information,\" or using our ", _jsx(OptOutLink, {
        onOpen: props.onOptOutClick,
        children: "Cookies Preferences Manager"
      }), ". If you do not have a Calm account, you may opt out by using your device settings. Specifically, on iOS devices, visit Settings > Privacy & Security > Tracking > Allow Apps to Request to Track. On Android devices, visit Settings > Google > Ads > Opt out of Ads Personalization."]
    }), _jsxs(_components.p, {
      children: ["Additionally, the online advertising industry also provides websites from which you may opt-out of receiving targeted ads from advertising partners that participate in self-regulatory programs. You can access these and also learn more about targeted advertising, consumer choice, and privacy at ", _jsx(_components.a, {
        href: "http://www.networkadvertising.org/managing/opt_out.asp",
        children: "www.networkadvertising.org/managing/opt_out.asp"
      }), " or ", _jsx(_components.a, {
        href: "http://aboutads.info/choices",
        children: "aboutads.info/choices"
      }), ". If you are in the EU, you may also visit: ", _jsx(_components.a, {
        href: "http://youronlinechoices.eu/",
        children: "youronlinechoices.eu"
      }), "."]
    }), _jsx(_components.p, {
      children: "To be clear, whether you are using our opt-out or an online industry opt-out, these cookie-based opt-outs must be performed on each device and browser that you wish to have opted-out. For example, if you have opted-out on your computer browser, that opt-out will not be effective on your mobile device. You must separately opt-out on each device."
    }), _jsxs(_components.p, {
      children: ["Additionally, we also use Google Analytics. For more information about Google Analytics, please visit ", _jsx(_components.a, {
        href: "https://policies.google.com/technologies/partner-sites",
        children: "https://policies.google.com/technologies/partner-sites"
      }), " or you can visit the following site to learn about their opt-out: ", _jsx(_components.a, {
        href: "http://tools.google.com/dlpage/gaoptout",
        children: "tools.google.com/dlpage/gaoptout"
      }), "."]
    }), _jsx(_components.h3, {
      children: "Contact Us"
    }), _jsxs(_components.p, {
      children: ["If you have any questions about this Cookie and Pixel Notice, please contact Calm by email at ", _jsx(_components.a, {
        href: "mailto:support@calm.com",
        children: "support@calm.com"
      }), "."]
    }), _jsx(_components.p, {
      children: "If you are in Europe, you may also contact our representative at:"
    }), _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "EEA:"
        }), " The DPO Centre Europe Ltd, Alexandra House, 3 Ballsbridge Park, Dublin, D04C 7H2 - (+353 1 631 9460) (", _jsx(_components.a, {
          href: "mailto:EURep@calm.com",
          children: "EURep@calm.com"
        }), ")."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "UK:"
        }), " The DPO Centre Ltd, 50 Liverpool Street, London, EC2M 7PR - (+44 (0) 203 797 1289) (", _jsx(_components.a, {
          href: "mailto:ukrep@calm.com",
          children: "ukrep@calm.com"
        }), ")."]
      }), "\n"]
    })]
  });
}
function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = Object.assign({}, _provideComponents(), props.components);
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, props)
  })) : _createMdxContent(props);
}
export default MDXContent;
